/**
 * AboPurchaseDetailPage — iter270
 *
 * Klickbare Kaufübersicht für ein Creator-/Premium-Abo.
 * Zeigt: Creator, Plan/Tier, Preis, Laufzeit, Status und die zugehörigen
 * Belege (Dokumente). Analog zur Social-Kaufübersicht — für ALLE Sparten klickbar.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import PaymentChoiceModal from "@/components/PaymentChoiceModal";
import {
  ArrowLeft, Loader2, FileText, Download, Sparkles, CheckCircle,
  Clock, XCircle, AlertOctagon, RotateCw,
} from "lucide-react";

function fmtEuro(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(2)} €`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return "—"; }
}

const DOC_LABEL = {
  SUBSCRIPTION_CONFIRMATION: "Abo-Bestätigung",
  SUBSCRIPTION_RENEWAL: "Verlängerungsbeleg",
  SUBSCRIPTION_CANCELLATION: "Kündigungsbestätigung",
  ORDER_CONFIRMATION: "Bestellübersicht",
  REFUND_RECEIPT: "Erstattungsbeleg",
};

const STATUS_BADGE = {
  ACTIVE: { label: "Aktiv", cls: "border-emerald-400 text-emerald-800 bg-emerald-50", Icon: CheckCircle },
  PENDING: { label: "Wartet auf Zahlung", cls: "border-slate-300 text-slate-700 bg-slate-50", Icon: Clock },
  EXPIRED: { label: "Abgelaufen", cls: "border-amber-300 text-amber-700 bg-amber-50", Icon: AlertOctagon },
  CANCELED: { label: "Gekündigt", cls: "border-red-300 text-red-700 bg-red-50", Icon: XCircle },
  CANCELLED: { label: "Gekündigt", cls: "border-red-300 text-red-700 bg-red-50", Icon: XCircle },
};

export default function AboPurchaseDetailPage() {
  const { t } = useTranslation();
  const { subscriptionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reactOpen, setReactOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data: d } = await api.get(`/me/subscriptions/${subscriptionId}`);
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail || "Abo nicht gefunden");
    } finally { setLoading(false); }
  }, [subscriptionId]);

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    load();
  }, [user, navigate, load]);

  const badge = STATUS_BADGE[data?.status] || { label: data?.status || "—", cls: "border-[#E5E5E5] text-[#666] bg-[#FAFAFA]", Icon: Sparkles };
  const planTitle = data?.plan?.title || data?.tier || "Premium-Abo";
  const isPastDue = data?.status === "PAST_DUE";

  const reactivate = async (method) => {
    const handle = data?.creator?.handle;
    const tier = (data?.tier || data?.plan?.tier || "").toString();
    if (!handle || !tier) throw new Error("fehlende Abo-Daten");
    const { data: res } = await api.post(
      `/creators/${handle}/subscribe/${tier}/checkout`,
      { payment_method: method, origin_url: window.location.origin },
    );
    if (res?.url) { window.location.href = res.url; return { redirected: true }; }
    return {};
  };

  const downloadDoc = async (docId, filename) => {
    try {
      const res = await api.get(`/documents/${docId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || `beleg_${docId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) {} }, 5000);
    } catch { window.alert(t("abo.receipt_load_error")); }
  };

  return (
    <div data-testid="abo-purchase-detail" className="w-full max-w-3xl mx-auto py-6 px-4">
      <button
        data-testid="abo-purchase-back"
        onClick={() => navigate("/profile/purchases")}
        className="flex items-center gap-2 text-sm text-[#666] hover:text-[#0A0A0A] mb-4"
      >
        <ArrowLeft size={16} /> {t("common.back", { defaultValue: "Zurück" })}
      </button>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-[#999]" /></div>
      ) : error ? (
        <p data-testid="abo-purchase-error" className="text-center text-sm text-red-600 py-16">{error}</p>
      ) : data ? (
        <div className="space-y-5">
          <div className="flex items-start gap-4">
            <div className="w-24 h-24 rounded-2xl bg-[#F5F5F5] overflow-hidden shrink-0 flex items-center justify-center">
              {data.creator?.avatar_url ? (
                <img src={data.creator.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : <Sparkles size={28} className="text-[#CCC]" />}
            </div>
            <div className="flex-1 min-w-0">
              <h1 data-testid="abo-purchase-title" className="text-xl font-bold text-[#0A0A0A]">{planTitle}</h1>
              <p className="text-sm text-[#666] mt-0.5">{data.creator?.display_name || data.creator?.handle}</p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span data-testid="abo-purchase-status" className={`text-[11px] px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${badge.cls}`}>
                  <badge.Icon size={11} /> {badge.label}
                </span>
                <span className="text-[11px] text-[#999]">{fmtDate(data.started_at)}</span>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-lg font-bold text-[#0A0A0A]">{fmtEuro(data.price ?? data.plan?.price_monthly_eur)}</p>
              <p className="text-[10px] text-[#999]">/ Monat</p>
            </div>
          </div>

          {isPastDue && (
            <div data-testid="abo-reactivate-banner" className="border border-amber-300 bg-amber-50 rounded-2xl p-4 flex items-start gap-3">
              <AlertOctagon size={18} className="text-amber-600 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900">{t("abo.paused_title")}</p>
                <p className="text-xs text-amber-800 mt-0.5">{t("abo.paused_desc")}</p>
                <button
                  data-testid="abo-reactivate-btn"
                  onClick={() => setReactOpen(true)}
                  className="mt-3 h-9 px-4 rounded-full bg-[#0A0A0A] text-white text-xs font-bold hover:bg-[#222] inline-flex items-center gap-2"
                >
                  <RotateCw size={13} /> Erneut aktivieren
                </button>
              </div>
            </div>
          )}

          <div className="border border-[#E5E5E5] rounded-2xl p-4">
            <h2 className="text-sm font-semibold text-[#0A0A0A] mb-3">{t("purchase_detail.overview", { defaultValue: "Kaufübersicht" })}</h2>
            <dl className="grid grid-cols-2 gap-y-2 text-sm">
              <dt className="text-[#666]">Abonummer</dt>
              <dd data-testid="abo-purchase-number" className="text-right text-[#0A0A0A]">{data.subscription_number || "—"}</dd>
              <dt className="text-[#666]">Zahlungsart</dt>
              <dd className="text-right text-[#0A0A0A]">{data.payment_method || "—"}</dd>
              <dt className="text-[#666]">Laufzeit bis</dt>
              <dd className="text-right text-[#0A0A0A]">{fmtDate(data.current_period_end)}</dd>
              {data.next_billing_at && (
                <>
                  <dt className="text-[#666]">{t("abo.next_charge")}</dt>
                  <dd className="text-right text-[#0A0A0A]">{fmtDate(data.next_billing_at)}</dd>
                </>
              )}
              {data.canceled_at && (
                <>
                  <dt className="text-[#666]">{t("abo.cancelled_on")}</dt>
                  <dd className="text-right text-[#0A0A0A]">{fmtDate(data.canceled_at)}</dd>
                </>
              )}
              <dt className="text-[#666]">Betrag</dt>
              <dd className="text-right font-semibold text-[#0A0A0A]">{fmtEuro(data.price ?? data.plan?.price_monthly_eur)}</dd>
            </dl>
          </div>

          {/* Belege */}
          <div className="border border-[#E5E5E5] rounded-2xl p-4">
            <h2 className="text-sm font-semibold text-[#0A0A0A] mb-3">{t("purchase_detail.documents", { defaultValue: "Belege" })}</h2>
            {(!data.documents || data.documents.length === 0) ? (
              <p data-testid="abo-purchase-no-docs" className="text-sm text-[#999]">{t("purchase_detail.no_documents", { defaultValue: "Noch keine Belege verfügbar." })}</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {data.documents.map((d) => (
                  <li key={d.document_id}>
                    <button
                      data-testid={`abo-purchase-doc-${d.document_id}`}
                      onClick={() => downloadDoc(d.document_id, d.filename)}
                      className="w-full flex items-center gap-3 border border-[#E5E5E5] rounded-xl p-3 hover:bg-[#FAFAFA] text-left"
                    >
                      <FileText size={18} className="text-[#0A0A0A] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#0A0A0A] truncate">{DOC_LABEL[d.doc_type] || d.doc_type || "Beleg"}</p>
                        <p className="text-xs text-[#666]">{d.document_number}</p>
                      </div>
                      <Download size={16} className="text-[#666] shrink-0" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button
            data-testid="abo-purchase-manage"
            onClick={() => navigate("/subscriptions")}
            className="w-full h-11 rounded-full border border-[#E5E5E5] text-sm text-[#0A0A0A] hover:bg-[#F5F5F5] transition-colors"
          >
            {t("purchase_detail.manage_subscriptions", { defaultValue: "Abos verwalten" })}
          </button>
        </div>
      ) : null}

      <PaymentChoiceModal
        open={reactOpen}
        title={`${planTitle} erneut aktivieren`}
        amount={data?.price ?? data?.plan?.price_monthly_eur}
        allowedMethods={["STRIPE", "PAYPAL"]}
        notice="Dein Abo wird mit der gewählten Zahlart erneut aktiviert und verlängert sich anschließend wieder automatisch. Jederzeit kündbar unter „Meine Käufe“."
        onClose={() => setReactOpen(false)}
        onConfirm={reactivate}
      />
    </div>
  );
}
